import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/apiService';
import About from './../pages/About';

const AuthContext = createContext();
const TOKEN_STORAGE_KEY = 'fsf_auth_token';
const USER_STORAGE_KEY = 'fsf_user';

const readStoredToken = () => {
  return sessionStorage.getItem(TOKEN_STORAGE_KEY);
};

const writeStoredToken = (token) => {
  if (!token) {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.removeItem(TOKEN_STORAGE_KEY);
};

const clearStoredAuth = () => {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
};

/**
 * 
 * @returns 
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = readStoredToken();

    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiService.getUser();
      setUser(response.user);
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'authentification:', error);
      clearStoredAuth();
    }

    setIsLoading(false);
  };

  const login = async (email, password) => {
    try {
      console.log('Tentative de connexion avec:', { email, password: '***' });
      // console.log('URL API utilisée:', 'http://localhost:8000/api/auth/login');
      
      const response = await apiService.login({ email, password });
      console.log('Réponse login complète:', response);
      
      if (response.user && response.token) {
        console.log('Connexion réussie, stockage des données...');
        setUser(response.user);
        sessionStorage.setItem('fsf_user', JSON.stringify(response.user));
        localStorage.removeItem('fsf_user');
        writeStoredToken(response.token);
        
        // Stocker le type de dashboard
        if (response.dashboard_type) {
          localStorage.setItem('fsf_dashboard_type', response.dashboard_type);
        }
        
        // Effacer le flag de déconnexion lors de la connexion
        localStorage.removeItem('fsf_logged_out');
        
        console.log('Données stockées, utilisateur connecté:', response.user);
        return { 
          success: true, 
          user: response.user, 
          dashboard_type: response.dashboard_type || 'main',
          redirect_url: response.redirect_url || '/dashboard'
        };
      } else {
        console.error('Réponse invalide:', response);
        throw new Error('Réponse d\'authentification invalide');
      }
    } catch (error) {
      console.error('Erreur de connexion détaillée:', error);
      console.error('Type d\'erreur:', typeof error);
      console.error('Message d\'erreur:', error.message);
      return { success: false, error: error.message || 'Erreur de connexion' };
    }
  };

  const logout = async () => {
    try {
      console.log('🚪 Début de la déconnexion...');
      await apiService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      console.log('🧹 Nettoyage de l\'état d\'authentification...');
      
      // Nettoyer l'état utilisateur
      setUser(null);
      
      // Nettoyer complètement le stockage d'authentification
      clearStoredAuth();
      
      // Marquer comme déconnecté pour éviter la reconnexion automatique en dev
      localStorage.setItem('fsf_logged_out', 'true');
      
      console.log('✅ Déconnexion terminée, redirection...');
      
      // Rediriger vers la page d'accueil
      window.location.href = '/';
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await apiService.forgotPassword(email);
      return { success: true, message: response.message };
    } catch (error) {
      console.error('Erreur mot de passe oublié:', error);
      return { success: false, error: error.message || 'Erreur lors de l\'envoi' };
    }
  };

  // Fonctions de vérification des rôles
  const isDashboardAuthorized = () => {
    if (!user || !user.role) return false;
    const authorizedRoles = ['admin', 'administrateur', 'apz', 'apd', 'moderateur', 'sgd', 'sgz', 'tgd', 'tgz', 'mcd', 'mcz'];
    return authorizedRoles.includes(user.role.toLowerCase());
  };

  const hasMainDashboardAccess = () => {
    if (!user || !user.role) return false;
    const mainDashboardRoles = ['admin', 'administrateur', 'apd', 'sgd', 'tgd', 'mcd'];
    return mainDashboardRoles.includes(user.role.toLowerCase());
  };

  const hasZoneDashboardAccess = () => {
    if (!user || !user.role) return false;
    const zoneDashboardRoles = ['apz', 'sgz', 'tgz', 'mcz', 'moderateur'];
    return zoneDashboardRoles.includes(user.role.toLowerCase());
  };

  // MCD et MCZ : consultation uniquement (ni création, ni modification, ni suppression)
  const isReadOnly = ['mcd', 'mcz'].includes(user?.role?.toLowerCase());

  const getDashboardRoute = () => {
    if (hasMainDashboardAccess()) {
      return '/dashboard';
    }
    if (user?.role?.toLowerCase() === 'membre') {
      return '/member-dashboard';
    }
    if (hasZoneDashboardAccess() || user?.zone) {
      return '/zone-dashboard';
    }
    return '/';
  };

  // === MÉTHODE DE REDIRECTION MODÉRATEUR ===
  // Identifie si l'utilisateur est modérateur et retourne l'URL de sa fraternité
  const getModerateurRedirectUrl = () => {
    if (!user) return null;
    
    // Vérifier si l'utilisateur est modérateur
    if (user?.role?.toLowerCase() === 'moderateur' || user?.role?.toLowerCase() === 'moderator') {
      console.log('🔐 Modérateur détecté');
      console.log('Fraternité:', user.fraternity);
      
      // Si le modérateur a une fraternité assignée, retourner l'URL de sa page
      if (user.fraternity) {
        const fraternityUrl = `/fraternity/${encodeURIComponent(user.fraternity)}/members`;
        console.log('📍 URL de redirection généré:', fraternityUrl);
        return fraternityUrl;
      }
    }
    
    return null;
  };

  // Rediriger le moderareur vers le dashboard de fraternité s'il a accès
  if (user?.role?.toLowerCase() === 'moderateur') {
    // return '/fraternity/Fraternité';
  }

  const getToken = () => {
    return readStoredToken();
  };

  const updateUserProfile = (updatedUserData) => {
    console.log('📝 Mise à jour du profil utilisateur:', updatedUserData);
    setUser(prevUser => {
      const newUser = { ...prevUser, ...updatedUserData };
      // Mettre à jour aussi le stockage de session pour persister les changements
      sessionStorage.setItem('fsf_user', JSON.stringify(newUser));
      localStorage.removeItem('fsf_user');
      return newUser;
    });
  };

  const value = {
    user,
    isLoading,
    login,
    logout,
    forgotPassword,
    updateUserProfile,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'administrateur',
    isDashboardAuthorized,
    isReadOnly,
    hasMainDashboardAccess,
    hasZoneDashboardAccess,
    getDashboardRoute,
    getModerateurRedirectUrl,
    token: getToken()
  };

  // Debug: affichons les informations utilisateur
  console.log('AuthContext - user:', user);
  console.log('AuthContext - isAuthenticated:', !!user);
  console.log('AuthContext - isAdmin:', user?.role === 'admin');
  console.log('AuthContext - isDashboardAuthorized:', isDashboardAuthorized());
  console.log('AuthContext - user role:', user?.role);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};